import { GeneratePromptResponseWithFileMutationRequest, OverviewPromptResponse } from '@/gen/types'
import { generatePromptResponse } from '@/gen/clients/prompt-controller/generatePromptResponse'
import { generatePromptResponseWithFile } from '@/gen/clients/prompt-controller/generatePromptResponseWithFile'
import { getUserTPrompts } from '@/gen/clients/me-controller/getUserTPrompts'
import { RequestConfig } from '@/api/axiosInstance'
import { PropertiesMap } from '@/types/generationPreferences'
import { useGenerationPreferencesStore } from '@/stores/generationPreferencesStore'
import { queryClient } from '@/queryClient'
import { waitForPromptCompletion } from '@/utils/polling'

export const TPROMPTS_QUERY_KEY = ['tprompts']

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

async function fetchAllPrompts() {
  return await queryClient.fetchQuery({
    queryKey: TPROMPTS_QUERY_KEY,
    queryFn: () => getUserTPrompts()
  })
}

/**
 * Returns the first prompt that exists in the user's prompt list.
 * Throws if none of the provided names are found.
 */
export async function resolvePrompt(...names: string[]) {
  const prompts = await fetchAllPrompts()
  for (const name of names) {
    const prompt = prompts.find(p => p.name === name)
    if (prompt) return prompt
  }
  throw new Error(`None of the prompts found: ${names.join(', ')}`)
}

/**
 * Low-level wrapper around /api/prompt with common logging.
 * Uses the generated SDK helper instead of raw axios.
 */
export async function invokePrompt(
  text: string,
  tprompt: string | undefined,
  properties: PropertiesMap,
  abortController?: AbortController,
  options?: { featureKey?: string }
): Promise<OverviewPromptResponse> {
  const apiProperties: { [key: string]: string } = {}
  for (const [key, value] of Object.entries(properties)) {
    apiProperties[key] = Array.isArray(value) ? value.join(',') : String(value)
  }

  const payload = { text, tprompt, featureKey: options?.featureKey, properties: apiProperties }
  const signal = abortController?.signal

  const response = await generatePromptResponse(payload, { signal })

  const status = response?.asyncTaskStatus?.taskStatus
  if (status && status !== 'FINISHED' && response?.uuid) {
    return await waitForPromptCompletion(response.uuid, signal)
  }

  return response
}

/**
 * Transcribe an audio file using the generated SDK helper.
 */
export async function postTranscribeAudio(
  file: Blob,
  config?: Partial<RequestConfig<GeneratePromptResponseWithFileMutationRequest>>
) {
  const uuid = (await resolvePrompt('Audio Transcription', 'Public Audio Transcription')).uuid
  return await generatePromptResponseWithFile({ file, tprompt: uuid }, config)
}

/**
 * Process a PDF or image file using Mistral OCR.
 */
export async function postOcrFile(
  file: Blob,
  config?: Partial<RequestConfig<GeneratePromptResponseWithFileMutationRequest>>
) {
  const uuid = (await resolvePrompt('Mistral OCR')).uuid
  return await generatePromptResponseWithFile({ file, tprompt: uuid }, config)
}

/**
 * Generate teil prompting for selected text within complete content.
 * Sends complete text as 'text' and selected text as 'TEIL' property.
 */
export async function postTeilPrompting(
  completeText: string,
  selectedText: string,
  action: string,
  abortController?: AbortController
): Promise<OverviewPromptResponse> {
  const { generationPreferences } = useGenerationPreferencesStore() // TODO; figure out if this maybe doesnt send the correct style and lang

  const properties: PropertiesMap = {
    ...generationPreferences,
    TEIL: [selectedText],
    ACTION: [action]
  }

  const legacyPromptNames = ['Teil Prompting', 'TeilPrompting']

  try {
    const { uuid } = await resolvePrompt(...legacyPromptNames)
    return await invokePrompt(completeText, uuid, properties, abortController)
  } catch (error) {
    console.warn('Teil Prompting prompt not found, falling back to feature key routing.', error)
    return await invokePrompt(completeText, undefined, properties, abortController, {
      featureKey: 'PROMPT_REWARD_TEXT'
    })
  }
}
